import { supabase } from '../lib/supabase';

export interface FiscalSettingsData {
  id?: string;
  tenant_cnpj: string;
  razao_social: string;
  nome_fantasia?: string;
  inscricao_estadual: string;
  inscricao_municipal?: string;
  crt: number; // 1 = Simples Nacional, 2 = Simples Excesso, 3 = Regime Normal
  regime_tributario_especial?: number;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  codigo_municipio: string;
  municipio: string;
  uf: string;
  cep: string;
  ambiente: number; // 1 = Produção, 2 = Homologação
  csc_token_id: string;
  csc_token_value?: string;
  certificate_valid_until?: string;
  serie_padrao: number;
  serie_padrao_nfe55?: number;
  serie_padrao_nfse?: number;
  danfe_layout_nfe55?: 'retrato' | 'paisagem';
  logo_url?: string;
  timeout_sefaz_seconds: number;
  nfce_enabled: boolean;
  nfe_model55_enabled?: boolean;
  nfse_enabled?: boolean;
  is_active: boolean;
  updated_at?: string;
}

export interface SaveFiscalSettingsPayload {
  tenantCnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  inscricaoEstadual: string;
  inscricaoMunicipal?: string;
  crt: number;
  regimeTributarioEspecial?: number;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  codigoMunicipio: string;
  municipio: string;
  uf: string;
  cep: string;
  ambiente: number;
  cscTokenId: string;
  cscTokenValue: string;
  certificatePfxBase64?: string;
  certificatePassword?: string;
  seriePadrao?: number;
  seriePadraoNfce?: number;
  seriePadraoNfe55?: number;
  seriePadraoNfse?: number;
  danfeLayoutNfe55?: 'retrato' | 'paisagem';
  logoUrl?: string;
  timeoutSefazSeconds?: number;
  nfceEnabled?: boolean;
  nfeModel55Enabled?: boolean;
  nfseEnabled?: boolean;
}

export interface FiscalCertificateStatus {
  isValid: boolean;
  isExpired: boolean;
  daysRemaining: number;
  validUntil: string;
  subjectName?: string;
  cnpj?: string;
  warningLevel: 'ok' | 'warning_30d' | 'critical_7d' | 'expired';
}

const getBackendUrl = () => {
  let backendUrl = import.meta.env.VITE_BACKEND_URL || '';
  // Ignora apenas o endpoint legado e descontinuado do Cloud Run
  if (backendUrl.includes('r-max-202214039096.europe-west1.run.app')) {
    backendUrl = '';
  }
  return backendUrl.replace(/\/+$/, '');
};

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  try {
    let { data: { session } } = await supabase.auth.getSession();

    // Renovação Silenciosa (Silent Refresh): se o token estiver ausente ou expirar em menos de 60s
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session?.expires_at || 0;
    if (!session?.access_token || (expiresAt > 0 && expiresAt - now < 60)) {
      const refreshed = await supabase.auth.refreshSession().catch(() => null);
      if (refreshed?.data?.session) {
        session = refreshed.data.session;
      }
    }

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch (err) {
    console.warn('[FiscalService] Erro ao obter/renovar token de autenticação:', err);
  }
  return headers;
};

export class FiscalService {
  private static _lastWarmupTime = 0;

  /**
   * Aquecimento Antecipado Silencioso (Pre-warming):
   * Dispara um ping leve no /healthz ao entrar no app ou abrir o caixa.
   * Se o Render estiver em cold start, acorda com antecedência enquanto o operador monta a venda.
   */
  public static prewarmBackend(): void {
    const backendUrl = getBackendUrl();
    if (!backendUrl) return;

    const now = Date.now();
    // Limita a 1 ping a cada 3 minutos por aba/sessão
    if (now - this._lastWarmupTime < 180000) return;
    this._lastWarmupTime = now;

    fetch(`${backendUrl}/healthz`, { method: 'GET', mode: 'cors' }).catch(() => {
      // Silencioso por design para não poluir a UX
    });
  }

  /**
   * Obtém as configurações fiscais do emitente
   */
  public static async getSettings(): Promise<FiscalSettingsData | null> {
    const headers = await getAuthHeaders();
    try {
      const response = await fetch(`${getBackendUrl()}/api/fiscal/settings`, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const json = await response.json();
        if (json?.data) return json.data;
      }
    } catch {
      // Backend HTTP indisponível, segue para fallback Supabase
    }

    // Fallback Offline-First / Direct Supabase via RLS
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const { data, error } = await supabase
          .from('fiscal_settings')
          .select('id, tenant_cnpj, razao_social, nome_fantasia, inscricao_estadual, crt, logradouro, numero, complemento, bairro, codigo_municipio, municipio, uf, cep, ambiente, csc_token_id, certificate_valid_until, serie_padrao, timeout_sefaz_seconds, nfce_enabled, is_active')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (!error && data) {
          return data as FiscalSettingsData;
        }
      }
    } catch (dbErr) {
      console.warn('[FiscalService] Fallback Supabase getSettings:', dbErr);
    }

    return null;
  }

  /**
   * Salva configurações fiscais (Certificado A1, CSC, dados da empresa)
   */
  public static async saveSettings(payload: SaveFiscalSettingsPayload): Promise<any> {
    const headers = await getAuthHeaders();
    let response: Response | null = null;
    try {
      response = await fetch(`${getBackendUrl()}/api/fiscal/settings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
    } catch {
      // Se não enviou novo certificado digital e o backend está offline, tenta fallback direto no Supabase
      if (!payload.certificatePfxBase64) {
        try {
          return await this.saveSettingsSupabaseFallback(payload);
        } catch {}
      }
      throw new Error(
        'Não foi possível conectar ao servidor fiscal. Verifique se o serviço de retaguarda Node.js está em execução.'
      );
    }

    let json: any = null;
    const contentType = response?.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      json = await response?.json().catch(() => null);
    }

    if (!response.ok) {
      // Se não enviou novo certificado e o backend retornou 405/404/500, salva cadastros direto no Supabase via RLS
      if (!payload.certificatePfxBase64) {
        try {
          return await this.saveSettingsSupabaseFallback(payload);
        } catch {}
      }

      const serverMsg = json?.error || json?.message;
      if (serverMsg) {
        throw new Error(serverMsg);
      }
      if (response.status === 405) {
        throw new Error(
          'Servidor fiscal de retaguarda indisponível (Status HTTP 405). A rota requer o serviço Node.js (packages/backend) em execução e a URL configurada em VITE_BACKEND_URL no Cloudflare Pages.'
        );
      }
      if (response.status === 404) {
        throw new Error(
          'Serviço fiscal não encontrado (Status 404). O servidor de retaguarda NFC-e precisa estar ativo para validar e assinar o certificado.'
        );
      }
      if (response.status === 502 || response.status === 503 || response.status === 504) {
        throw new Error(
          `Servidor fiscal indisponível no momento (Status ${response.status}). Tente novamente em instantes.`
        );
      }
      throw new Error(`Erro na resposta do servidor fiscal (Status HTTP ${response.status}).`);
    }

    if (!json || !json.success) {
      throw new Error(json?.error || 'Falha ao salvar configurações fiscais.');
    }

    return json;
  }

  /**
   * Fallback de atualização direta via Supabase RLS quando o backend Node.js não estiver conectado
   * (permite atualizar dados cadastrais, endereço, alíquotas e séries sem reprocessar o certificado PFX)
   */
  private static async saveSettingsSupabaseFallback(payload: SaveFiscalSettingsPayload): Promise<any> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    const cleanCnpj = payload.tenantCnpj.replace(/\D/g, '');
    const cleanIE = payload.inscricaoEstadual.replace(/\D/g, '');
    const cleanCep = (payload.cep || '').replace(/\D/g, '');
    const cleanIbge = (payload.codigoMunicipio || '').replace(/\D/g, '');

    const updatePayload: Record<string, any> = {
      tenant_cnpj: cleanCnpj,
      razao_social: payload.razaoSocial.trim(),
      nome_fantasia: (payload.nomeFantasia || payload.razaoSocial).trim(),
      inscricao_estadual: cleanIE,
      inscricao_municipal: payload.inscricaoMunicipal?.trim() || null,
      crt: Number(payload.crt || 1),
      regime_tributario_especial: Number(payload.regimeTributarioEspecial || 0),
      logradouro: payload.logradouro.trim(),
      numero: String(payload.numero || 'S/N').trim(),
      complemento: payload.complemento?.trim() || null,
      bairro: payload.bairro.trim(),
      codigo_municipio: cleanIbge,
      municipio: payload.municipio.trim(),
      uf: (payload.uf || 'SP').trim().toUpperCase(),
      cep: cleanCep,
      ambiente: Number(payload.ambiente || 2),
      csc_token_id: payload.cscTokenId ? String(payload.cscTokenId).trim() : null,
      serie_padrao: Number(payload.seriePadraoNfce || payload.seriePadrao || 1),
      serie_padrao_nfe55: Number(payload.seriePadraoNfe55 || 1),
      serie_padrao_nfse: Number(payload.seriePadraoNfse || 1),
      danfe_layout_nfe55: payload.danfeLayoutNfe55 === 'paisagem' ? 'paisagem' : 'retrato',
      logo_url: payload.logoUrl?.trim() || null,
      timeout_sefaz_seconds: Number(payload.timeoutSefazSeconds || 5),
      nfce_enabled: Boolean(payload.nfceEnabled),
      nfe_model55_enabled: Boolean(payload.nfeModel55Enabled),
      nfse_enabled: Boolean(payload.nfseEnabled),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('fiscal_settings')
      .update(updatePayload)
      .eq('user_id', session.user.id)
      .select('id, tenant_cnpj, razao_social, ambiente, certificate_valid_until, serie_padrao, nfce_enabled, inscricao_municipal, nfse_enabled, nfe_model55_enabled, danfe_layout_nfe55, logo_url')
      .maybeSingle();

    if (error) {
      throw new Error(`Falha ao atualizar dados fiscais no banco: ${error.message}`);
    }

    if (!data) {
      throw new Error('Configurações fiscais não localizadas para atualização.');
    }

    return {
      success: true,
      message: 'Configurações fiscais da empresa salvas com sucesso no banco de dados.',
      data,
    };
  }

  /**
   * Chave Liga/Desliga Rápida da Emissão de Cupom Fiscal
   */
  public static async toggleFiscal(enabled: boolean): Promise<{ success: boolean; message: string; nfce_enabled: boolean }> {
    const headers = await getAuthHeaders();
    try {
      const response = await fetch(`${getBackendUrl()}/api/fiscal/toggle`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ enabled }),
      });

      if (response.ok) {
        const json = await response.json().catch(() => null);
        if (json?.success) {
          return {
            success: true,
            message: json.message,
            nfce_enabled: json.data?.nfce_enabled ?? enabled,
          };
        }
      }
    } catch {
      // Backend inacessível, tenta fallback direto do Supabase
    }

    // Fallback Direto Supabase via RLS
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    const { data, error } = await supabase
      .from('fiscal_settings')
      .update({ nfce_enabled: enabled, updated_at: new Date().toISOString() })
      .eq('user_id', session.user.id)
      .select('nfce_enabled')
      .maybeSingle();

    if (error) {
      throw new Error(`Não foi possível alterar a chave fiscal: ${error.message}`);
    }

    return {
      success: true,
      message: enabled
        ? 'Emissão fiscal (NFC-e) ATIVADA no caixa.'
        : 'Emissão fiscal DESATIVADA. O caixa imprimirá comprovantes não-fiscais.',
      nfce_enabled: data?.nfce_enabled ?? enabled,
    };
  }

  /**
   * Dispara a emissão de NFC-e na SEFAZ-SP (ou documento não-fiscal se a chave estiver desligada)
   */
  public static async emitNFCe(salePayload: any): Promise<any> {
    const headers = await getAuthHeaders();
    let response: Response;
    try {
      response = await fetch(`${getBackendUrl()}/api/fiscal/emit-nfce`, {
        method: 'POST',
        headers,
        body: JSON.stringify(salePayload),
      });
    } catch {
      throw new Error('Não foi possível conectar ao servidor para emissão da NFC-e.');
    }

    const json = await response.json().catch(() => ({ error: 'Falha ao processar resposta do servidor fiscal.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha na emissão da NFC-e');
    }

    return json;
  }

  /**
   * Converte um recibo salvo em NFC-e e realiza a emissão retroativa na SEFAZ
   */
  public static async emitFromReceipt(receiptId: string): Promise<any> {
    const headers = await getAuthHeaders();
    let response: Response;
    try {
      response = await fetch(`${getBackendUrl()}/api/fiscal/emit-from-receipt`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ receiptId }),
      });
    } catch {
      throw new Error('Não foi possível conectar ao servidor para emissão da NFC-e a partir do recibo.');
    }

    const json = await response.json().catch(() => ({ error: 'Falha ao processar resposta do servidor fiscal.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha na emissão da NFC-e a partir do recibo');
    }

    return json;
  }

  /**
   * Consulta o status em tempo real dos servidores da SEFAZ
   */
  public static async checkSefazStatus(): Promise<{
    success: boolean;
    online: boolean;
    cStat?: string;
    motivo?: string;
    ambiente?: number;
    uf?: string;
    dhRecbto?: string;
    tMed?: string;
    error?: string;
  }> {
    const headers = await getAuthHeaders();
    try {
      const response = await fetch(`${getBackendUrl()}/api/fiscal/status-sefaz`, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        return await response.json();
      }
      const errJson = await response.json().catch(() => ({}));
      return {
        success: false,
        online: false,
        error: errJson.error || 'Erro retornado pelo servidor fiscal.',
      };
    } catch (err: any) {
      return {
        success: false,
        online: false,
        error: err.message || 'Falha de rede ao verificar status da SEFAZ.',
      };
    }
  }

  /**
   * Dispara a emissão de NF-e Modelo 55 (B2B / Grande com Frete e Duplicatas)
   */
  public static async emitNFe55(payload: any): Promise<any> {
    const headers = await getAuthHeaders();
    let response: Response;
    try {
      response = await fetch(`${getBackendUrl()}/api/fiscal/emit-nfe55`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
    } catch {
      throw new Error('Não foi possível conectar ao servidor para emissão da NF-e Modelo 55.');
    }

    const json = await response.json().catch(() => ({ error: 'Falha ao processar resposta do servidor fiscal.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha na emissão da NF-e Modelo 55');
    }

    return json;
  }

  /**
   * Dispara a emissão de NFS-e Nacional (Padrão ADN / Receita Federal) para Serviços e OS
   */
  public static async emitNFSe(payload: any): Promise<any> {
    const headers = await getAuthHeaders();
    let response: Response;
    try {
      response = await fetch(`${getBackendUrl()}/api/fiscal/emit-nfse`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
    } catch {
      throw new Error('Não foi possível conectar ao servidor para emissão da NFS-e Nacional.');
    }

    const json = await response.json().catch(() => ({ error: 'Falha ao processar resposta do servidor fiscal.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha na emissão da NFS-e Nacional');
    }

    return json;
  }

  /**
   * Consulta a validade e status do Certificado Digital A1
   */
  public static async getCertificateStatus(): Promise<FiscalCertificateStatus | null> {
    const headers = await getAuthHeaders();
    try {
      const response = await fetch(`${getBackendUrl()}/api/fiscal/certificate-status`, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const json = await response.json();
        if (json?.data) return json.data;
      }
    } catch {
      // Falha de rede ou backend offline
    }
    return null;
  }

  /**
   * Cancela um documento fiscal (NFC-e 65, NF-e 55 ou NFS-e Nacional)
   */
  public static async cancelDocument(payload: {
    chaveAcesso: string;
    modelo: '55' | '65' | 'nfse';
    protocolo?: string;
    justificativa: string;
  }): Promise<any> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/api/fiscal/cancel`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const json = await response.json().catch(() => ({ error: 'Falha ao processar cancelamento.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha ao cancelar documento fiscal.');
    }

    return json;
  }

  /**
   * Inutiliza faixa de numeração na SEFAZ (Modelo 55 ou 65)
   */
  public static async inutNumber(payload: {
    ano: number;
    modelo: '55' | '65';
    serie: number;
    numeroInicial: number;
    numeroFinal: number;
    justificativa: string;
  }): Promise<any> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/api/fiscal/inut-number`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const json = await response.json().catch(() => ({ error: 'Falha ao inutilizar numeração.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha ao inutilizar numeração na SEFAZ.');
    }

    return json;
  }

  /**
   * Registra Carta de Correção Eletrônica (CC-e) para NF-e Modelo 55
   */
  public static async sendCCe(payload: {
    chaveAcesso: string;
    sequenciaEvento: number;
    correcao: string;
  }): Promise<any> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/api/fiscal/cce`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const json = await response.json().catch(() => ({ error: 'Falha ao enviar CC-e.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha ao enviar Carta de Correção à SEFAZ.');
    }

    return json;
  }

  /**
   * Baixa o PDF do Documento Auxiliar (DANFE / DANFSe / DACCe)
   */
  public static async downloadDocumentPdf(documentId: string, filename?: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/api/fiscal/documents/${documentId}/pdf`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Não foi possível obter o arquivo PDF do documento fiscal.');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `danfe_${documentId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Lista documentos emitidos
   */
  public static async listDocuments(limit: number = 20, offset: number = 0): Promise<any> {
    const headers = await getAuthHeaders();
    try {
      const response = await fetch(`${getBackendUrl()}/api/fiscal/documents?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        return await response.json();
      }
    } catch {}

    // Fallback Supabase via RLS
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      return { success: false, data: [], total: 0 };
    }

    const { data, count, error } = await supabase
      .from('fiscal_documents')
      .select('id, modelo, serie, numero, chave_acesso, ambiente, tipo_emissao, data_emissao, valor_total, status, cstat, motivo_status, protocolo_autorizacao, qr_code_url, created_at', { count: 'exact' })
      .eq('user_id', session.user.id)
      .order('data_emissao', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Falha ao listar documentos fiscais: ${error.message}`);
    }

    return {
      success: true,
      data: data || [],
      total: count || 0,
    };
  }

  /**
   * Baixa o XML oficial protocolado de uma nota fiscal
   */
  public static async downloadDocumentXml(documentId: string, filename?: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/api/fiscal/documents/${documentId}/xml`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Não foi possível obter o arquivo XML da nota fiscal.');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `documento_${documentId}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Reemite uma nota fiscal rejeitada a partir do payload original de venda
   * Por padrão, preserva o mesmo número fiscal (keepSameNumber: true) conforme Ajustes SINIEF nº 40/2026 e 38/2026.
   */
  public static async retryRejectedDocument(documentId: string, keepSameNumber = true): Promise<any> {
    const headers = await getAuthHeaders();
    let response: Response;
    try {
      response = await fetch(`${getBackendUrl()}/api/fiscal/documents/${documentId}/retry`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keepSameNumber }),
      });
    } catch {
      throw new Error('Não foi possível conectar ao servidor para reemissão da nota fiscal.');
    }

    const json = await response.json().catch(() => ({ error: 'Falha ao processar resposta do servidor fiscal.' }));
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Falha na reemissão da nota fiscal.');
    }

    return json;
  }

  /**
   * Obtém o resumo de fechamento fiscal do mês para a contabilidade
   */
  public static async getMonthlyClosing(ano: string, mes: string): Promise<any> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/api/fiscal/monthly-closing?ano=${ano}&mes=${mes}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Falha ao obter fechamento fiscal mensal.');
    }

    return await response.json();
  }
}
